import React from "react";
import {
  MapContainer,
  TileLayer,
  Rectangle,
  Tooltip,
  GeoJSON,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import * as d3 from "d3";
import * as turf from "@turf/turf";

const MapVisualization = ({
  response,
  selectedVariable,
  onLocationSelect,
  boundaryGeoJson,
}) => {
  // 1. Data Validation: Ensures all necessary data props exist.
  if (
    !response ||
    !response.data ||
    !response.coords ||
    !response.coords.latitude ||
    !response.coords.longitude ||
    !response.data[selectedVariable]
  ) {
    return <p style={{ color: "gray" }}>No valid data to display on map.</p>;
  }

  const lats = response.coords.latitude;
  const lons = response.coords.longitude;
  const timeSeries = response.data[selectedVariable];

  if (!Array.isArray(timeSeries)) {
    return <p style={{ color: "red" }}>Invalid time series data.</p>;
  }

  // 2. Extract latest data slice.
  const latestSlice = timeSeries[timeSeries.length - 1];

  if (!latestSlice || !Array.isArray(latestSlice)) {
    return (
      <p style={{ color: "orange" }}>No numeric data available to visualize.</p>
    );
  }

  // 3. Flatten the data to find min/max values for the color scale.
  const allValues = latestSlice
    .flat()
    .filter((value) => typeof value === "number" && !isNaN(value));
  if (allValues.length === 0) {
    return (
      <p style={{ color: "orange" }}>No numeric data available to visualize.</p>
    );
  }

  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const colorScale = d3
    .scaleSequential(d3.interpolatePuBuGn)
    .domain([min, max]);

  // 4. Generate the grid of rectangles
  const gridRectangles = [];
  for (let i = 0; i < lats.length - 1; i++) {
    for (let j = 0; j < lons.length - 1; j++) {
      const rawValue = latestSlice[i]?.[j];
      const value = typeof rawValue === "number" ? rawValue : Number(rawValue);

      if (!isFinite(value)) continue;

      const bounds = [
        [lats[i], lons[j]],
        [lats[i + 1], lons[j + 1]],
      ];

      gridRectangles.push(
        <Rectangle
          key={`${i}-${j}`}
          bounds={bounds}
          pathOptions={{
            color: "transparent",
            fillColor: colorScale(value),
            fillOpacity: 0.7,
            weight: 0,
          }}
          eventHandlers={{
            click: () => {
              if (onLocationSelect) {
                onLocationSelect({ lat: lats[i], lon: lons[j] });
              }
            },
          }}
        >
          <Tooltip direction="top" offset={[0, -5]} opacity={1}>
            <div>
              <strong>Value:</strong> {value.toFixed(2)} <br />
              <strong>Lat:</strong> {lats[i].toFixed(2)}°<br />
              <strong>Lon:</strong> {lons[j].toFixed(2)}°
            </div>
          </Tooltip>
        </Rectangle>
      );
    }
  }

  // 5. Calculate the map's center coordinates.
  const centerLat = lats[Math.floor(lats.length / 2)];
  const centerLon = lons[Math.floor(lons.length / 2)];

  return (
    <div style={{ height: "600px", marginTop: "20px" }}>
      <MapContainer
        center={[centerLat, centerLon]}
        zoom={8}
        scrollWheelZoom={true}
        style={{ height: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {boundaryGeoJson && (
          <GeoJSON
            data={boundaryGeoJson}
            style={{
              color: "green",
              weight: 2,
              fillOpacity: 0.05,
            }}
          />
        )}

        {/* Render the grid of colored rectangles */}
        {gridRectangles}
      </MapContainer>
    </div>
  );
};

export default MapVisualization;
